import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getCatalog,
  getSearchCatalogCount,
  SEARCH_OPTIONS,
} from '../apis/relevant-catalog';
import { IFilter } from './use-services-filters';
import { Catalog } from '../apis/search-catalog';
import {
  useMostAccessedCatalogsByFields,
  UseMostAccessedCatalogsByFieldsParams,
} from './catalog/most-accessed-catalogs';

interface IQueryProps {
  filters: IFilter;
  itemsPerPage: number;
  initialData?: CatalogQueryData;
  accessToken?: string;
}

export type CatalogQueryData = {
  items: Catalog[];
  totalPages: number;
  shouldClampCurrentPage?: boolean;
  isTotalLoading?: boolean;
};

const MULTIFIELD_PLACEHOLDER_PAGE_COUNT = 6;

export const useCatalogQuery = ({
  filters,
  initialData,
  itemsPerPage,
  accessToken,
}: IQueryProps) => {
  const from = (filters.currentPage - 1) * itemsPerPage;
  const isMostAccessedSearch =
    !!filters.searchQuery &&
    !filters.selectedCategory &&
    !filters.selectedDepartment &&
    !filters.selectedTag;

  const mostAccessedCatalogsParams =
    useMemo<UseMostAccessedCatalogsByFieldsParams>(() => {
      const params: UseMostAccessedCatalogsByFieldsParams = {
        query: isMostAccessedSearch ? filters.searchQuery : '',
        limit: itemsPerPage,
      };

      if (filters.digital) {
        params.isDigital = true;
        params.hasLink = true;
      }

      if (filters.scheduled) {
        params.isOnline = true;
        params.hasLink = true;
      }

      return params;
    }, [
      filters.digital,
      filters.scheduled,
      filters.searchQuery,
      isMostAccessedSearch,
      itemsPerPage,
    ]);

  const mostAccessedCatalogs = useMostAccessedCatalogsByFields(
    mostAccessedCatalogsParams
  );

  const mostAccessedCountQuery = useQuery<number>({
    queryKey: [
      'most-accessed-catalogs-count',
      JSON.stringify(mostAccessedCatalogsParams),
    ],
    queryFn: () =>
      getSearchCatalogCount({
        q: mostAccessedCatalogsParams.query,
        isDigital: mostAccessedCatalogsParams.isDigital,
        isOnline: mostAccessedCatalogsParams.isOnline,
        hasLink: mostAccessedCatalogsParams.hasLink,
      }),
    enabled: isMostAccessedSearch,
    staleTime: 1000 * 60 * 5,
    retry: true,
  });

  const catalogQuery = useQuery<CatalogQueryData>({
    queryKey: ['catalog', filters],
    queryFn: async () => {
      const queryParams: Partial<SEARCH_OPTIONS> = { from, size: itemsPerPage };

      if (filters.selectedCategory) {
        queryParams.category = [filters.selectedCategory];
      } else if (filters.selectedDepartment) {
        queryParams.departmentSlug = [filters.selectedDepartment];
      } else if (filters.selectedTag) {
        queryParams.tags = [filters.selectedTag];
      } else if (filters.searchQuery) {
        queryParams.query = filters.searchQuery;
      }

      if (filters.digital) {
        queryParams.isDigital = true;
        queryParams.hasLink = true;
      }

      if (filters.scheduled) {
        queryParams.isOnline = true;
        queryParams.hasLink = true;
      }

      return await getCatalog(accessToken, queryParams);
    },
    enabled: !isMostAccessedSearch,
    staleTime: 1000 * 60 * 5,
    retry: true,
    placeholderData: initialData,
  });

  const requiredItems = filters.currentPage * itemsPerPage;
  const {
    data: mostAccessedItems,
    hasMore: mostAccessedHasMore,
    isLoading: isMostAccessedLoading,
    isFetchingMore: isMostAccessedFetchingMore,
    isSuccess: isMostAccessedSuccess,
    isError: isMostAccessedError,
    loadMore: loadMoreMostAccessed,
  } = mostAccessedCatalogs;

  useEffect(() => {
    if (
      isMostAccessedSearch &&
      mostAccessedHasMore &&
      !isMostAccessedLoading &&
      !isMostAccessedFetchingMore &&
      mostAccessedItems.length < requiredItems
    ) {
      loadMoreMostAccessed();
    }
  }, [
    isMostAccessedSearch,
    isMostAccessedFetchingMore,
    isMostAccessedLoading,
    loadMoreMostAccessed,
    mostAccessedHasMore,
    mostAccessedItems.length,
    requiredItems,
  ]);

  const mostAccessedData = useMemo(() => {
    const isTotalResolving = mostAccessedCountQuery.isFetching;
    const fallbackTotalPages = Math.max(
      1,
      Math.ceil(mostAccessedItems.length / itemsPerPage)
    );
    const hasResolvedTotalItems =
      !isTotalResolving &&
      !mostAccessedCountQuery.isError &&
      mostAccessedCountQuery.data !== undefined;
    const resolvedTotalPages = hasResolvedTotalItems
      ? Math.max(1, Math.ceil(mostAccessedCountQuery.data / itemsPerPage))
      : fallbackTotalPages;
    const totalPages = !hasResolvedTotalItems && mostAccessedHasMore
      ? Math.max(
          filters.currentPage + MULTIFIELD_PLACEHOLDER_PAGE_COUNT - 1,
          resolvedTotalPages
        )
      : resolvedTotalPages;
    return {
      items: mostAccessedItems.slice(from, from + itemsPerPage).map((item) => ({
        ...item,
        resultTotal: hasResolvedTotalItems
          ? mostAccessedCountQuery.data
          : item.resultTotal,
      })),
      totalPages,
      isTotalLoading: isTotalResolving,
      shouldClampCurrentPage:
        !mostAccessedHasMore && filters.currentPage > resolvedTotalPages,
    };
  }, [
    filters.currentPage,
    from,
    itemsPerPage,
    mostAccessedHasMore,
    mostAccessedItems,
    mostAccessedCountQuery.data,
    mostAccessedCountQuery.isError,
    mostAccessedCountQuery.isFetching,
  ]);

  if (isMostAccessedSearch) {
    return {
      ...catalogQuery,
      data: mostAccessedData,
      isLoading:
        isMostAccessedLoading ||
        isMostAccessedFetchingMore ||
        (mostAccessedHasMore && mostAccessedItems.length < requiredItems),
      isSuccess: isMostAccessedSuccess,
      isError: isMostAccessedError,
    };
  }

  return catalogQuery;
};
